import { Injectable } from '@angular/core';
import { dbConst } from './databaseConstants';
import { IUser } from '../../models/user';
import { collection, collectionData, Firestore, query, where, limit, doc, docData, updateDoc, setDoc, addDoc, docSnapshots, Timestamp, getDoc, arrayUnion } from '@angular/fire/firestore';
import { Observable, of } from 'rxjs';
import DalHelper from '../helpers/string.helper';
import { FirestoreCrudService } from './crudService';
import { AuthService } from '../auth.service';
import { catchError, filter, map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  constructor(
    private firestore: Firestore,
  ) { }


  selectUserByEmail(email: string) {
    // The users collection is keyed on uid (doc-id = uid). Consumers read
    // user.uid, so place the doc-id on `uid` (not `id`) — otherwise uid is
    // missing for users without an explicitly stored uid field. limit(2) also
    // cut off matches when multiple accounts share the same email.
    const listRef = collection(this.firestore, dbConst.users);
    const q = query(listRef, where('email', '==', email), limit(25));
    return collectionData(q, { idField: 'uid' }) as Observable<IUser[]>;
  }

  getItem(userId: string) {
    const itemPath = DalHelper.getItemPath(dbConst.users, userId);

    const docRef = doc(this.firestore, itemPath);
    return docData(docRef, { idField: 'id' })
      .pipe(
        map((value: any) => {
          const data = value as any;
          Object.keys(data).filter(key => Boolean(data[key]?.seconds))
            .forEach(key => {
              if (data[key].seconds) {
                const date = new Timestamp(data[key].seconds, data[key].nanoseconds);
                data[key] = date.toDate();
              }
            });
          Object.keys(data).filter(key => data[key] instanceof Timestamp)
            .forEach(key => data[key] = data[key].toDate())
          return data as IUser;
        }),
        catchError((error: unknown) => {
          console.error(`stream caught ${itemPath}: ${error}`);
          return of(null);
        })
      );
  }


  getMetadataUser(userId: string) {
    const itemPath = DalHelper.getItemPath(dbConst.metadata, userId);
    const docRef = doc(this.firestore, itemPath);
    return docData(docRef, { idField: 'id' })
      .pipe(
        filter((data) => Boolean(data)),
        map((value: any) => {
          const data = value as any;
          Object.keys(data).filter(key => Boolean(data[key]?.seconds))
            .forEach(key => {
              if (data[key].seconds) {
                const date = new Timestamp(data[key].seconds, data[key].nanoseconds);
                data[key] = date.toDate();
              }
            });
          Object.keys(data).filter(key => data[key] instanceof Timestamp)
            .forEach(key => data[key] = data[key].toDate())
          return data;
        }),
        catchError((error: unknown) => {
          console.error(`stream caught : ${error}`);
          return of(null);
        })
      );



  }

  async setMetaDataUser(userId: string) {
    const itemPath = DalHelper.getItemPath(dbConst.metadata, userId);
    const date = new Date();
    const item = doc(this.firestore, itemPath);
    await updateDoc(item, { refreshTime: date });
  }

  async update(item: IUser) {
    const itemPath = DalHelper.getItemPath(dbConst.users, item.uid);

    const userDoc = doc(this.firestore, itemPath);

    if (userDoc !== null) {
      return setDoc(userDoc, item);
    }

    const collRef = collection(this.firestore, dbConst.users);
    return addDoc(collRef, item);
  }

  async setNotificationToken(uid: string, notificationToken: string): Promise<void> {
    if (!uid || !notificationToken) {
      return;
    }
    const itemPath = DalHelper.getItemPath(dbConst.users, uid);
    const userDoc = doc(this.firestore, itemPath);
    const snap = await getDoc(userDoc);
    if (!snap.exists()) {
      return;
    }
    await updateDoc(userDoc, { notificationTokens: arrayUnion(notificationToken) });
  }
}
